import React from 'react';
import { Button } from '../ui/button';
import { Edit, Trash2, Package } from 'lucide-react';
import AddOnsManager from '../../pages/AddOnsManager';
import ConsultationFormsManager from '../../pages/ConsultationFormsManager';
import { ServiceProductsManager } from './ServiceProductsManager';
import { formatDuration } from '../../utils/format';
import { EmptyState } from './EmptyState';

const LIMIT = 10;

const PAYMENT_LABELS = {
  full: 'Full payment',
  deposit_50: '50% deposit',
  minimum_deposit: 'Min. deposit',
};

export function ServicesTab({ services, setServiceDialogOpen, editService, handleDeleteService }) {
  const [showAll, setShowAll] = React.useState(false);
  const displayed = showAll ? services : services.slice(0, LIMIT);
  const hasMore = services.length > LIMIT;

  return (
    <>
      <div className="card-surface p-4 sm:p-6">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
          <h3 className="font-heading text-xl font-medium text-[#1A1A1A]">Your Services</h3>
          {services.length > 0 && (
            <span className="text-sm text-stone-400">{services.length} total</span>
          )}
        </div>
        {services.length === 0 ? (
          <EmptyState
            icon={Package}
            testId="services-empty-state"
            title="Add your first service"
            body="Clients can only book what they can see. Start with the service you are best known for, you can add more any time."
            actionLabel="Add your first service"
            onAction={() => setServiceDialogOpen(true)}
          />
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {displayed.map((service) => (
                <div key={service.service_id} className="border border-[#E6E4DD] rounded-xl p-4 sm:p-6 hover:shadow-md transition-shadow">
                  <h4 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">{service.name}</h4>
                  <p className="text-sm text-stone-500 mb-4 line-clamp-2">{service.description}</p>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-stone-500">{formatDuration(service.duration)}</span>
                    <span className="font-semibold text-[#D4AF37]">£{service.price}</span>
                  </div>
                  {service.payment_type && service.payment_type !== 'full' && (
                    <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                      {PAYMENT_LABELS[service.payment_type] || service.payment_type}
                    </span>
                  )}
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => editService(service)}>
                      <Edit className="h-4 w-4 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDeleteService(service.service_id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            {hasMore && (
              <div className="mt-6 text-center">
                <Button
                  variant="outline"
                  onClick={() => setShowAll(!showAll)}
                  className="border-[#E6E4DD] text-stone-600 hover:text-[#1A1A1A]"
                  data-testid="services-view-all-btn"
                >
                  {showAll ? 'Show Less' : `View All ${services.length} Services`}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <div className="card-surface p-4 sm:p-6 mt-6" data-testid="addons-section">
        <AddOnsManager services={services} />
      </div>
      <div className="card-surface p-4 sm:p-6 mt-6" data-testid="service-products-section">
        <ServiceProductsManager services={services} />
      </div>
      <div className="card-surface p-4 sm:p-6 mt-6" data-testid="consultation-forms-section">
        <ConsultationFormsManager services={services} />
      </div>
    </>
  );
}
